<template>
  <div style="width: 100%; height: 100%;">
    <div style="position: relative; display: block; width: 100%; height: 100%;">
      <pui-common-spinner
        ref="masonrySpinner"
        v-if="isLoading"
        :size="20"
        :strokeWidth="2"
        :hideMessage="true"
        position="fixed"
        class="brightcove-spinner"
      />
      <div
        id="viewer-container"
        ref="viewerContainer"
        v-if="!viewerInError"
        style="width: 100%; height: 100%;"
      ></div>
      <div v-else style="width: 100%; height: 100%;">
        <default-viewer :file="file" :getMediaUrl="getMediaUrl" />
      </div>
    </div>
  </div>
</template>
<script>
import DefaultViewer from '../../../dam/components/viewers/DefaultViewer';
import players from '../api/players';

const EXPRESSION_VIDEO = '<video.*<\/video>';
const EXPRESSION_SCRIPT = '<script.*<\/script>';
const EXPRESSION_SRC = 'src="(.+)"';

export default {
  name: 'LmfrBrightCoveViewer',
  components: { DefaultViewer },
  props: {
    /**
     * File object
     * @type Object
     */
    file: {
      type: Object,
      required: true,
    },
    /**
     * Function to get media url in order to display default viewer in error case
     * @type Function
     */
    getMediaUrl: {
      type: Function,
      required: true,
    },
    /**
     * Attributes values file. Needed to get brightcoveId
     * @type Array
     */
    attributes: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      scriptElement: null,
      brightCoveId: null,
      viewerInError: false,
      isLoading: true,
    };
  },
  watch: {
    /**
     * Watch the attributes array to load video viewer
     * @param {Object} nextVal - the next value
     * @param {Object} oldVal - the previous value
     * @returns {void}
     */
    attributes(newVal, oldVal) {
      // If the attributes change we have to reload the viewer
      // since the viewer is defined by an attribute
      if (newVal.length && newVal !== oldVal) {
        this.isLoading = true;
        this.viewerInError = false;
        this.getBrightCoveViewer();
      }
    },
    file(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.isLoading = true;
        this.viewerInError = false;
        this.removeAllChildNodes(this.$refs.viewerContainer);
      }
    },
  },
  methods: {
    /**
     * Call API to get bright cove viewer infos
     */
    async getBrightCoveViewer() {
      if (this.file && this.attributes && this.attributes.length) {
        const brightCoveAttr = this.attributes.find(attr => attr.name === 'IDBrightCove');
        if (brightCoveAttr) {
          this.brightCoveId = brightCoveAttr.value;
        }
        if (this.brightCoveId && this.brightCoveId !== '') {
          try {
            let responseViewer = await players.getBrightCoveViewer(this.brightCoveId);
            responseViewer = responseViewer.body.replaceAll('\n', '');

            const regexVideo = new RegExp(EXPRESSION_VIDEO);
            const regexScript = new RegExp(EXPRESSION_SCRIPT);
            const regexSrc = new RegExp(EXPRESSION_SRC);

            const videoElementStr = regexVideo.exec(responseViewer)
              ? regexVideo.exec(responseViewer)[0]
              : null;
            const scriptElement = regexScript.exec(responseViewer)
              ? regexScript.exec(responseViewer)[0]
              : null;
            const srcElementStr = regexSrc.exec(scriptElement)
              ? regexSrc.exec(scriptElement)[1]
              : null;

            if (videoElementStr && srcElement) {
              await this.injectElement(videoElementStr, srcElementStr);
              this.isLoading = false;
            } else {
              this.isLoading = false;
              throw new Error();
            }
          } catch (error) {
            this.isLoading = false;
            this.viewerInError = true;
          }
        } else {
          this.isLoading = false;
          this.viewerInError = true;
        }
      }
    },
    /**
     * Inject element video and script in DOM
     * @param {String} video - the tag video in string to inject in DOM
     * @param {String} src - player src to create element script in DOM
     * @returns {void}
     */
    async injectElement(videoStr, srcStr) {
      try {
        const viewerContainer = this.$refs.viewerContainer;
        this.removeAllChildNodes(viewerContainer);
        const videoElement = new DOMParser().parseFromString(videoStr, 'text/html').body
          .childNodes[0];
        if (videoElement) {
          this.removeAllChildNodes(viewerContainer);
          viewerContainer.appendChild(videoElement);
          this.scriptElement = document.createElement('script');
          this.scriptElement.src = srcStr;
          document.head.appendChild(this.scriptElement);
        } else {
          throw new Error('did not an element from the video string');
        }
      } catch (error) {
        throw error;
      }
    },
    removeAllChildNodes(parent) {
      while (parent.firstChild) {
        parent.removeChild(parent.firstChild);
      }
    },
  },
  /**
   * Before destroy hook
   */
  beforeDestroy() {
    if (this.scriptElement) {
      this.scriptElement.parentNode.removeChild(this.scriptElement);
    }
  },
};
</script>
